import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { aiEntitlementResource } from '@jira/router-resources__ai-entitlement/src/aiEntitlementResource.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { incidentExperimentTraitResource } from '@jira/router-resources__software-incidents/src/incidentExperimentTraitResource.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { forgeProjectModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { softwareCalendarClassicRoute } from '@jira/router-routes__classic-projects-routes/src/softwareCalendarClassicRoute.tsx';
import { CalendarSkeleton } from '@jira/platform__skeletons/src/ui/calendar/CalendarSkeleton.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { classicSoftwareProjectLayout } from '@jira/platform__layouts-software-project-layout/src/index.tsx';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { projectThemeResource } from '@jira/router-resources__project-theme/src/projectThemeResource.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Route } from '@atlassian/react-resource-router';
import { softwareCalendarRouteEntry } from './ui/software/calendar/index.tsx';

export const softwareCalendarClassicRouteEntry: Route = createEntry(
	softwareCalendarClassicRoute,
	() => ({
		group: 'classic-software',
		skeleton: CalendarSkeleton,
		// eslint-disable-next-line no-restricted-syntax
		component: CalendarSkeleton,
		entryPoint() {
			// TODO: Adopt sidebar entrypoints when calender route enables SSR
			return softwareCalendarRouteEntry;
		},
		layout: composeLayouts(
			classicSoftwareProjectLayout,
			embeddedIssuePreloadLayoutBuilder([{ query: ['selectedIssue'] }]),
		),
		navigation: {
			menuId: MenuIdProjects,
			horizontal: AsyncHorizontalNavJira,
		},
		resources: [
			...getIssueViewResources(),
			...getNavigationResources(), // these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
			// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
			incidentExperimentTraitResource,
			// eslint-disable-next-line jira/router/no-project-context-resource
			projectContextResource,
			forgeProjectModuleResource,
			staticProjectSidebarNavigationResource,
			staticProjectSidebarConnectAddonsNavigationResource,
			// end of resources expanded from getNavigationSidebarProjectResource
			projectThemeResource,
			resourceWithCondition(() => fg('ai_sprint_goal_gate'), aiEntitlementResource),
		],
		afterPaint: [LazyIssueApp],
		forPaint: [LazyAtlassianNavigation],
		ufoName: 'classic-calendar',
		EXPERIMENTAL__shouldReload: ({ context, prevContext, pluginId }) => {
			if (fg('dfo_connectedtoissueaggquery')) {
				if (pluginId === 'resources-plugin') {
					return (
						context.match.url !== prevContext.match.url ||
						(context.query.selectedIssue !== undefined &&
							context.query.selectedIssue !== prevContext.query.selectedIssue)
					);
				}

				return context.match.url !== prevContext.match.url;
			}
			return context.match.url !== prevContext.match.url;
		},
		supportsPreviewPanel: true,
		supportsProjectTheme: 'full',
		bifrost: {
			bifrostGroup: 'shell-only',
		},
	}),
);
